import type { automation as en } from "../en/automation";
import type { Messages } from "../types";

/** ALP-83: منتقيات الوسوم والفئات، إجراءات الوكلاء، إحصاءات وسوم القاعدة. */
const E1_AR = {
  tagPicker: {
    label: "الوسوم والفئات الذكية",
    add: "إضافة",
    search: "ابحث أو أنشئ…",
    builtIn: "وسوم مدمجة",
    custom: "وسومك",
    categories: "فئات الذكاء الاصطناعي",
    noCustom: "لا توجد وسوم بعد",
    noCategories: "لا توجد فئات ذكاء اصطناعي بعد",
    noMatch: "لا نتائج مطابقة",
    create: "إنشاء «{{label}}»",
    createTag: "إنشاء وسم",
    createCategory: "إنشاء فئة ذكاء اصطناعي",
    newTitle: "وسم جديد",
    newCategoryTitle: "فئة ذكاء اصطناعي جديدة",
    name: "الاسم",
    namePlaceholder: "مثلاً: إيجابي",
    color: "اللون",
    isCategory: "فئة ذكاء اصطناعي",
    isCategoryHint: "يصنّف الذكاء الاصطناعي التعليقات الجديدة بها (مع درجة ثقة) اعتماداً على الوصف والأمثلة.",
    description: "ماذا تعني",
    descriptionPlaceholder: "مثلاً: العميل يريد استرداد أمواله",
    examples: "أمثلة",
    examplesPlaceholder: "اكتب مثالاً ثم اضغط Enter",
    save: "إنشاء",
    cancel: "إلغاء",
    created: "تم إنشاء «{{label}}» واختياره",
    failed: "تعذّر الإنشاء",
    remove: "إزالة {{label}}",
    manage: "إدارة الوسوم",
    categoryBadge: "ذكاء",
  },
  agentPicker: {
    label: "الوكيل",
    placeholder: "اختر وكيلاً",
    none: "لا يوجد وكلاء بعد",
    create: "إنشاء وكيل",
    unavailable: "تعذّر تحميل الوكلاء",
    paused: "متوقف",
    modeDraft: "يصوغ الردود لموافقتك",
    modeAuto: "يرسل الردود تلقائياً",
    private: "الرد بشكل خاص (رسالة خاصة واحدة لكل تعليق)",
    privateHint: "يسمح إنستغرام برد خاص واحد لكل تعليق خلال 7 أيام.",
    dmHint: "تُرسل فقط ضمن نافذة المراسلة في إنستغرام (24 ساعة).",
    legacy: "قديم",
    legacyHint: "يرد بالوكيل المُسنَد إلى المحادثة أو الحساب. لاختيار وكيل محدد استخدم «الرد بوكيل ذكي».",
    errAgent: "اختر وكيلاً.",
  },
  conditionsExtra: {
    minSentimentConfidence: "فقط عندما تكون ثقة الذكاء الاصطناعي على الأقل",
    anyConfidence: "أي درجة ثقة",
    categoryHint: "تحتاج فئاتك الخاصة (مثل إيجابي أو يريد استرداداً)؟",
    createCategory: "إنشاء فئة ذكاء اصطناعي",
    categoryConfidence: "الحد الأدنى للثقة",
    errTags: "اختر وسماً واحداً على الأقل.",
    errCategories: "اختر فئة ذكاء اصطناعي واحدة على الأقل.",
  },
  tagStats: {
    title: "ما وسمته هذه القاعدة",
    summary: "وسمت هذه القاعدة {{week}} تعليقاً هذا الأسبوع · {{all}} منذ البداية",
    week: "هذا الأسبوع",
    allTime: "منذ البداية",
    byTag: "حسب الوسم",
    posts: "عبر هذه المنشورات",
    none: "لم يُوسم شيء بعد. ستظهر المطابقات هنا.",
    openInbox: "فتح في صندوق الوارد",
    openPost: "عرض التعليقات الموسومة في هذا المنشور",
    count_zero: "لا تعليقات",
    count_one: "تعليق واحد",
    count_two: "تعليقان",
    count_few: "{{count}} تعليقات",
    count_many: "{{count}} تعليقًا",
    count_other: "{{count}} تعليق",
    cardBadge_zero: "لا شيء موسوم",
    cardBadge_one: "عنصر واحد موسوم",
    cardBadge_two: "عنصران موسومان",
    cardBadge_few: "{{count}} عناصر موسومة",
    cardBadge_many: "{{count}} عنصرًا موسومًا",
    cardBadge_other: "{{count}} عنصر موسوم",
  },
};

type CALM_EN_SHAPE = Pick<typeof en, "rulesTitle" | "rulesDescription" | "logLink" | "newMenu" | "card" | "firstUse" | "steps" | "advanced" | "advancedHint" | "moreConditions" | "nameAndLook" | "more" | "viewJson" | "hideJson" | "testTitle" | "testHint" | "testCommentsOnly" | "fixList" | "goToField" | "errorName" | "errorConditions" | "errorActions" | "errorActionsEmpty" | "errorOther" | "notifyInApp" | "unavailable" | "log">;

/** ALP-84 (slice P3): calmer rules list, builder sections, automation log in words. */
const CALM_AR: Messages<CALM_EN_SHAPE> = {
  rulesTitle: "القواعد",
  rulesDescription: "القواعد ترد على التعليقات والرسائل أو تخفيها أو توسمها نيابةً عنك: تلقائياً، أو بعد موافقتك، أو كاقتراحات.",
  logLink: "سجل الأتمتة",
  newMenu: {
    describe: "صِفها بكلماتك",
    describeHint: "اكتب ما يجب أن يحدث، والذكاء الاصطناعي يصوغ القاعدة.",
    template: "ابدأ من قالب",
    templateHint: "قواعد مجرّبة لإنستغرام، جاهزة للتعديل.",
    blank: "قاعدة فارغة",
    blankHint: "ابنِها خطوة بخطوة.",
  },
  card: {
    sentence: "عندما {{trigger}} وكان {{condition}}: {{actions}}.",
    ran_zero: "لم تعمل هذا الأسبوع",
    ran_one: "عملت مرة واحدة هذا الأسبوع",
    ran_two: "عملت مرتين هذا الأسبوع",
    ran_few: "عملت {{count}} مرات هذا الأسبوع",
    ran_many: "عملت {{count}} مرة هذا الأسبوع",
    ran_other: "عملت {{count}} مرة هذا الأسبوع",
    waiting_zero: "لا شيء بانتظار الموافقة",
    waiting_one: "عنصر واحد بانتظار الموافقة",
    waiting_two: "عنصران بانتظار الموافقة",
    waiting_few: "{{count}} عناصر بانتظار الموافقة",
    waiting_many: "{{count}} عنصرًا بانتظار الموافقة",
    waiting_other: "{{count}} عنصر بانتظار الموافقة",
    on: "تعمل",
    off: "متوقفة",
    openLog: "فتح السجل",
    turnOn: "تشغيل",
    turnOff: "إيقاف",
  },
  firstUse: "القواعد ترد على التعليقات أو تخفيها أو توسمها نيابةً عنك. اختر قالباً للبدء، أو صِف قاعدتك.",
  steps: {
    when: "عندما",
    whenHint: "ما الذي يشغّل القاعدة، وأين.",
    if: "إذا",
    ifHint: "اتركها فارغة لتعمل على كل شيء.",
    then: "فنفّذ",
    thenHint: "ما يفعله ملفك، بالترتيب.",
    how: "الطريقة",
    howHint: "نفّذ فوراً، أو اسأل أولاً، أو اقترح فقط.",
  },
  advanced: "خيارات متقدمة",
  advancedHint: "الأولوية والحد في الساعة وساعات الهدوء",
  moreConditions: "شروط أخرى",
  nameAndLook: "الاسم والشكل",
  more: "المزيد",
  viewJson: "عرض كـ JSON",
  hideJson: "إخفاء JSON",
  testTitle: "اختبر هذه القاعدة",
  testHint: "جرّب القاعدة على أحدث تعليقاتك قبل تشغيلها. لا يُرسل أي شيء.",
  testCommentsOnly: "يُجرى الاختبار على التعليقات الأخيرة فقط.",
  fixList: "أصلح ما يلي قبل الحفظ",
  goToField: "انتقل إلى الحقل",
  errorName: "الاسم: أعطِ القاعدة اسماً.",
  errorConditions: "إذا: بعض الشروط غير مكتملة.",
  errorActions: "فنفّذ: أحد الإجراءات ينقصه تفصيل.",
  errorActionsEmpty: "فنفّذ: أضف إجراءً واحداً على الأقل.",
  errorOther: "شيء ما في القاعدة غير صالح بعد.",
  notifyInApp: "يرسل تنبيهاً داخل التطبيق",
  unavailable: {
    comingSoon: "قريباً",
    notifyChannels: "تُرسل الإشعارات داخل التطبيق فقط حالياً؛ البريد وSlack وTelegram قريباً.",
  },
  log: {
    title: "سجل الأتمتة",
    description: "كل ما فعلته قواعدك بكلمات بسيطة: ما رأته وما حدث.",
    whatHappened: "ما حدث",
    filters: "التصفية",
    columns: "الأعمدة",
    sentence: "{{action}} · {{event}}",
    sentenceNoAction: "اطّلعت على {{event}}",
    commentWord: "تعليق",
    messageWord: "رسالة",
    noAction: "بلا إجراء",
    technical: "تفاصيل تقنية",
    eventType: "نوع الحدث",
    staffTrace: "فتح تتبّع الذكاء الاصطناعي (للفريق)",
  },
};

export const automation: Messages<typeof en> = {
  ...CALM_AR,
  title: "القواعد",
  description: "قواعد تقرأ كل تعليق ورسالة جديدة وتتصرّف بصوت علامتك التجارية: تلقائياً، أو بعد موافقتك، أو كاقتراحات.",
  newRule: "قاعدة جديدة",
  runsLink: "سجل الأتمتة",
  list: {
    active: "مفعّلة",
    paused: "متوقفة",
    activeCount: "{{n}} مفعّلة",
    priority: "الأولوية {{n}}",
    matched: "تطابق",
    executed: "نُفّذ",
    pending: "معلّق",
    last7: "آخر 7 أيام",
    edit: "تعديل القاعدة",
    duplicate: "نسخ",
    delete: "حذف القاعدة",
    deleted: "تم حذف القاعدة",
    toggledOn: "«\u2068{{name}}\u2069» مفعّلة الآن",
    toggledOff: "«\u2068{{name}}\u2069» متوقفة",
    when: "عند",
    if: "إذا",
    then: "فإن",
    emptyTitle: "لا توجد قواعد بعد",
    emptyHint: "ابدأ من قالب أو صِف ما تريده بكلمات بسيطة. يمكن اختبار كل قاعدة على التعليقات الأخيرة قبل تفعيلها.",
    allAccounts: "كل الحسابات",
    onePost: "منشور واحد",
    filterAll: "الكل",
    filterActive: "المفعّلة",
    filterPaused: "المتوقفة",
    searchPlaceholder: "ابحث في القواعد…",
    noMatch: "لا توجد قواعد تطابق هذه التصفية",
  },
  prompt: {
    title: "صِف القاعدة، والذكاء الاصطناعي يصوغها",
    hint: "مثلاً: «عندما يسأل أحد عن السعر، رُدّ بأننا أرسلنا التفاصيل وأرسل له قائمة الأسعار على الخاص.»",
    placeholder: "عندما يقوم أحدهم…",
    submit: "صِغ القاعدة",
    drafting: "جارٍ الصياغة…",
    examples: "جرّب:",
    example1: "أخفِ التعليقات المزعجة والروابط",
    example2: "عندما يسألون عن السعر، راسلهم على الخاص",
    example3: "التعليقات الغاضبة تنتظر موافقتي",
    example4: "إذا علّق أحد «دليل» أرسل له الدليل",
    tooShort: "صِف القاعدة بكلمات أكثر قليلاً.",
  },
  templates: {
    title: "ابدأ من قالب",
    hint: "أتمتة مجرّبة لإنستغرام. يمكنك تعديل كل شيء قبل الحفظ.",
    use: "استخدم القالب",
  },
  triggers: {
    comment_created: "تعليق جديد",
    message_received: "رسالة خاصة جديدة",
    mention: "إشارة",
    story_reply: "رد على ستوري",
    postback: "ضغطة زر",
  },
  modes: {
    auto: "تلقائي",
    approve: "بعد الموافقة",
    suggest: "اقتراح فقط",
    autoHint: "ينفّذ ملفك الإجراء فوراً. الأنسب للرسائل المزعجة والردود البسيطة الآمنة.",
    approveHint: "يصوغ ملفك الإجراء وينتظر في قائمة الموافقات حتى يوافق شخص.",
    suggestHint: "يقترح ملفك رداً في صندوق الوارد فقط، ولا يُرسل شيء.",
  },
  ops: {
    all: "كل ما يلي",
    any: "أيٌّ مما يلي",
    not: "ليس",
    keyword: "كلمة مفتاحية",
    regex: "نمط نصي (متقدم)",
    language: "اللغة",
    intent: "النية (ذكاء اصطناعي)",
    sentiment: "المشاعر",
    ai_prompt: "الذكاء الاصطناعي يحكم على وصف",
    first_time_contact: "أول تواصل",
    is_reply: "رد على تعليق",
    has_link: "يحتوي رابطاً",
    commenter_marked: "صاحب التعليق مُصنَّف",
    has_tag: "يحمل وسماً",
    custom_category: "فئة ذكية",
    commenter_blocked: "صاحب التعليق محظور في منصة ملفك",
  },
  opHints: {
    keyword: "يتطابق إذا احتوى النص على أي كلمة (أو ساواها تماماً).",
    regex: "متقدّم: تعبير نمطي يُختبر دون تمييز حالة الأحرف.",
    language: "اللغة المكتشفة للتعليق أو الرسالة.",
    intent: "ما يريده الشخص، يصنّفه الذكاء الاصطناعي مع نسبة ثقة.",
    sentiment: "نبرة الرسالة.",
    ai_prompt: "صِف الشرط بكلمات بسيطة والذكاء الاصطناعي يقرّر. أبطأ، لذا يعمل في الأخير.",
    first_time_contact: "هذا الشخص لم يعلّق أو يراسل من قبل.",
    is_reply: "التعليق رد على تعليق آخر.",
    has_link: "النص يحتوي رابطاً أو نطاقاً.",
    commenter_marked: "صنّف فريقك هذا الشخص مؤذياً أو متنمّراً معروفاً أو مميزاً.",
    has_tag: "يحمل العنصر أحد هذه الوسوم أو الفئات الذكية.",
    custom_category: "تصنيفاتك الخاصة بالذكاء الاصطناعي (مثل إيجابي أو سلبي أو يريد استرداداً) مع درجة ثقة.",
    commenter_blocked: "كل القواعد الأخرى تتجاهل المحظورين؛ القواعد التي فيها هذا الشرط تعمل لهم، لكنها لا ترد ولا تستخدم الذكاء الاصطناعي أبداً.",
  },
  summary: {
    always: "كل حدث",
    keyword: "النص يحتوي {{words}}",
    keywordExact: "النص يساوي {{words}}",
    regex: "النص يطابق /{{pattern}}/",
    language: "اللغة {{list}}",
    intent: "النية {{list}} ({{conf}} أو أكثر)",
    sentiment: "المشاعر {{list}}",
    sentimentConf: "المشاعر {{list}} ({{conf}} أو أكثر)",
    ai_prompt: "الذكاء: «⁨{{text}}⁩»",
    first_time_contact: "أول تواصل",
    is_reply: "رد على تعليق",
    has_link: "يحتوي رابطاً",
    commenter_marked: "صاحب التعليق مُصنَّف {{list}}",
    has_tag: "يحمل الوسم {{list}}",
    custom_category: "الفئة الذكية {{list}} ({{conf}} أو أكثر)",
    commenter_blocked: "صاحب التعليق محظور في منصة ملفك",
    not: "ليس ({{inner}})",
    and: " و ",
    or: " أو ",
    empty: "لا شيء بعد",
    listOr: " أو ",
  },
  actions: {
    reply_public: "رد علني",
    reply_private: "رد خاص (رسالة)",
    send_message: "إرسال رسالة خاصة",
    ai_agent_reply: "الرد بالوكيل المُسنَد",
    hide: "إخفاء التعليق",
    delete: "حذف التعليق",
    tag: "وسم الشخص",
    assign: "إسناد لزميل",
    notify: "تنبيه",
    flag: "وسم العنصر",
    mark_commenter: "تصنيف صاحب التعليق",
    enrich_commenter: "جلب ملف صاحب التعليق",
    reply_with_agent: "الرد بوكيل ذكي",
    dm_with_agent: "رسالة خاصة بوكيل ذكي",
    summaryFlag: "وسم {{flags}}",
    summaryAgent: "الرد عبر {{agent}}",
    summaryAgentPrivate: "الرد الخاص عبر {{agent}}",
    summaryAgentDm: "رسالة خاصة عبر {{agent}}",
    summaryMark: "تصنيف صاحب التعليق {{mark}}",
    summaryTag: "وسم {{tags}}",
    summaryAssign: "إسناد إلى {{name}}",
    summaryNotify: "تنبيه عبر {{channels}}",
    aiPersonalized: "مخصّص بالذكاء",
  },
  marks: {
    harmful: "مؤذٍ",
    troll: "متنمّر معروف",
    vip: "مميز",
  },
  flagTarget: {
    item: "التعليق أو المحادثة",
    contact: "الشخص",
  },
  notifyChannels: {
    in_app: "داخل التطبيق",
    email: "البريد",
    slack: "سلاك",
    telegram: "تيليغرام",
  },
  builder: {
    newTitle: "قاعدة جديدة",
    editTitle: "تعديل القاعدة",
    back: "القواعد",
    basics: "الأساسيات",
    name: "اسم القاعدة",
    namePlaceholder: "مثلاً: أسئلة السعر ← رسالة خاصة",
    icon: "الأيقونة",
    color: "اللون",
    trigger: "عندما يحدث هذا",
    scope: "أين",
    scopeAccount: "الحساب",
    scopeAllAccounts: "كل الحسابات المربوطة",
    scopePost: "على منشور واحد فقط",
    scopeAnyPost: "أي منشور",
    conditions: "إذا تطابقت هذه الشروط",
    conditionsHint: "الفحوصات السريعة تعمل أولاً، وفحوصات الذكاء الاصطناعي أخيراً.",
    addCondition: "إضافة شرط",
    addGroup: "إضافة مجموعة",
    removeCondition: "إزالة الشرط",
    wrapNot: "عكس (ليس)",
    unwrapNot: "إزالة «ليس»",
    groupAll: "يجب أن تتطابق الكل",
    groupAny: "يكفي أن يتطابق أيٌّ منها",
    switchToAll: "التبديل إلى «الكل»",
    switchToAny: "التبديل إلى «أيٌّ»",
    emptyGroup: "مجموعة فارغة — أضف شرطاً",
    words: "الكلمات",
    wordsPlaceholder: "اكتب كلمة واضغط Enter",
    contains: "يحتوي",
    exact: "مطابق تماماً",
    pattern: "النمط",
    testText: "نص للتجربة",
    testMatches: "يتطابق",
    testNoMatch: "لا يتطابق",
    invalidRegex: "نمط غير صالح",
    minConfidence: "أدنى نسبة ثقة",
    describe: "صِف الشرط",
    describePlaceholder: "مثلاً: الشخص يبدو غاضباً أو مستاءً من التوصيل",
    noOptions: "لا خيارات لهذا الشرط.",
    actions: "فنفّذ ما يلي",
    actionsHint: "تُنفّذ الإجراءات بالترتيب. القاعدة هي التي تحدّد الإجراء، وليس الذكاء الاصطناعي.",
    addAction: "إضافة إجراء",
    moveUp: "تحريك للأعلى",
    moveDown: "تحريك للأسفل",
    removeAction: "إزالة الإجراء",
    template: "الرسالة",
    templatePlaceholder: "اكتب الرد…",
    variables: "إدراج:",
    aiPersonalize: "دع الذكاء الاصطناعي يخصّصها",
    aiPersonalizeHint: "يعيد الذكاء الاصطناعي صياغة الرسالة لكل شخص بصوت علامتك.",
    agent: "الوكيل الذكي",
    agentNames: { "dm-agent": "الوكيل الذكي للرسائل", "sales-agent": "الوكيل الذكي للمبيعات", "support-agent": "الوكيل الذكي للدعم" },
    tags: "الوسوم",
    tagsPlaceholder: "أضف وسماً",
    assignee: "الزميل",
    pickAssignee: "اختر زميلاً",
    channels: "القنوات",
    mode: "كيف يتصرّف ملفك؟",
    igRules: "تنطبق قواعد إنستغرام: رد خاص واحد لكل تعليق خلال 7 أيام، والرسائل الخاصة فقط خلال 24 ساعة من آخر رسالة للشخص.",
    limits: "حدود الأمان",
    perHour: "{{n}} إجراء كحد أقصى في الساعة",
    quietHours: "ساعات الهدوء",
    quietHoursHint: "لا إجراءات تلقائية في هذه الفترة، وتنتظر التطابقات حتى تنتهي.",
    from: "من",
    to: "إلى",
    timezone: "المنطقة الزمنية",
    priority: "الأولوية",
    priorityHint: "الأرقام الأصغر تعمل أولاً عند تطابق عدة قواعد.",
    active: "مفعّلة",
    activeHint: "يمكن محاكاة القواعد المتوقفة أيضاً.",
    save: "حفظ القاعدة",
    saving: "جارٍ الحفظ…",
    created: "تم إنشاء القاعدة",
    saved: "تم حفظ القاعدة",
    deleteConfirm: "حذف «\u2068{{name}}\u2069»؟ يبقى سجلها في سجل الأتمتة.",
    unsaved: "لديك تغييرات غير محفوظة. هل تريد المغادرة؟",
    invalid: "صحّح الحقول المحدّدة قبل الحفظ.",
    errName: "أعطِ القاعدة اسماً.",
    errActions: "أضف إجراءً واحداً على الأقل.",
    errConditions: "بعض الشروط غير مكتملة.",
    errAssignee: "اختر زميلاً.",
    errTags: "أضف وسماً واحداً على الأقل.",
    errTemplate: "اكتب الرسالة.",
    errChannels: "اختر قناة واحدة على الأقل.",
    json: "JSON",
    showJson: "عرض JSON",
    hideJson: "إخفاء JSON",
    fromPrompt: "صاغها الذكاء الاصطناعي من وصفك. راجعها وجرّب المحاكاة ثم احفظ.",
    fromTemplate: "بدأت من قالب. راجعها وجرّب المحاكاة ثم احفظ.",
    notFound: "هذه القاعدة لم تعد موجودة.",
  },
  simulate: {
    title: "اختبر هذه القاعدة",
    hint: "تشغيل تجريبي لهذه القاعدة (غير المحفوظة) على أحدث تعليقاتك. لا يُرسل شيء.",
    run: "شغّل المحاكاة",
    rerun: "أعد التشغيل",
    running: "جارٍ المحاكاة…",
    sample: "حجم العينة",
    matched: "تطابق",
    notMatched: "لم يتطابق",
    matchRate: "{{matched}} من {{total}} ستتطابق",
    empty: "شغّل المحاكاة لترى التعليقات التي ستلتقطها هذه القاعدة.",
    noResults: "لا توجد تعليقات حديثة في هذا النطاق.",
    showAll: "الكل",
    showMatched: "المتطابقة",
    showUnmatched: "غير المتطابقة",
    stale: "تغيّرت القاعدة منذ آخر تشغيل.",
    reason: "السبب",
  },
  runs: {
    title: "سجل الأتمتة",
    description: "كل ما فعلته قواعدك بكلمات بسيطة: ما رأته وما حدث.",
    rule: "القاعدة",
    allRules: "كل القواعد",
    result: "النتيجة",
    allResults: "كل النتائج",
    event: "الحدث",
    labels: "الفئات الذكية",
    action: "الإجراء",
    when: "الوقت",
    trace: "الأثر",
    openTrace: "فتح الأثر",
    noTrace: "لا يوجد أثر",
    matched: "تطابق",
    notMatched: "لم يتطابق",
    details: "التفاصيل",
    hideDetails: "إخفاء التفاصيل",
    loadMore: "تحميل المزيد",
    emptyTitle: "لا يوجد تنفيذ بعد",
    emptyHint: "يظهر التنفيذ هنا بمجرد أن تفحص قاعدة تعليقاً أو رسالة جديدة.",
    confidence: "الثقة",
    language: "اللغة",
    toxicity: "الإساءة",
    eventComment: "تعليق",
    eventMessage: "رسالة",
    deleted: "قاعدة محذوفة",
    results: {
      executed: "تم",
      pending_approval: "بانتظار الموافقة",
      skipped: "تخطّي",
      failed: "فشل",
      suggested: "اقتراح",
    },
  },
  ...E1_AR,
};
